import { RefreshCw } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { ApiError } from '@/api/http';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { canReloadForChunkError, getChunkReloadState, isChunkLoadError, reloadForChunkError, subscribeChunkReload } from '@/lib/chunkReload';

/** Shown while the first screen's code loads, and while the app reloads itself. */
export function AppSplash({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" className="flex min-h-dvh items-center justify-center p-6 text-sm text-muted">
      {label}
    </div>
  );
}

/** Why the page is showing: a chunk that failed with no network is not a new version. */
type Cause = 'chunk' | 'chunkOffline' | 'other';

function titleOf(error: unknown, cause: Cause): string {
  if (cause === 'chunkOffline') return 'No connection';
  if (cause === 'chunk') return 'A new version of the app is available';
  if (isRouteErrorResponse(error) && error.status === 404) return 'Page not found';
  return 'Something went wrong';
}

function readableMessage(error: unknown, cause: Cause): string {
  if (cause === 'chunkOffline') return 'Part of the app could not be loaded. It reloads by itself when the connection is back.';
  if (cause === 'chunk') return 'Part of the app could not be loaded. Reload to get the latest version.';
  if (isRouteErrorResponse(error) && error.status === 404) return 'There is no page at this address.';
  if (isRouteErrorResponse(error)) return `The page could not be shown (${error.status}${error.statusText ? ` ${error.statusText}` : ''}).`;
  if (error instanceof ApiError && error.message.trim()) return error.message;
  if (error instanceof Error && error.message.trim()) return error.message;
  return 'Something unexpected happened.';
}

function ErrorPanel({ fullPage }: { fullPage: boolean }) {
  const error = useRouteError();
  const chunk = isChunkLoadError(error);
  // Decided once per error: ask for an automatic reload unless one happened in the last 30 seconds.
  const [autoReload] = useState(() => chunk && canReloadForChunkError());
  const reload = useSyncExternalStore(subscribeChunkReload, getChunkReloadState, getChunkReloadState);

  useEffect(() => {
    // Reloads only once the server answers; offline it waits for the connection while the error shows.
    if (autoReload) void reloadForChunkError();
  }, [autoReload]);

  // 'idle' with autoReload: the effect above has not asked yet.
  if (reload === 'checking' || reload === 'reloading' || (autoReload && reload === 'idle')) return <AppSplash label="Updating the app…" />;

  const cause: Cause = !chunk ? 'other' : reload === 'waitingForNetwork' ? 'chunkOffline' : 'chunk';

  const stack = import.meta.env.DEV && error instanceof Error ? error.stack : undefined;

  return (
    <div className={cn('flex justify-center', fullPage && 'min-h-dvh items-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]')}>
      <div role="alert" className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-surface p-5">
        {fullPage ? (
          <h1 className="text-lg font-semibold text-balance">{titleOf(error, cause)}</h1>
        ) : (
          <h2 className="text-lg font-semibold text-balance">{titleOf(error, cause)}</h2>
        )}
        <p className="text-sm text-pretty break-words text-muted">{readableMessage(error, cause)}</p>
        {stack && <pre className="max-h-48 overflow-auto rounded-lg bg-surface-2 p-2 text-xs break-words whitespace-pre-wrap text-muted">{stack}</pre>}
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>
            <RefreshCw aria-hidden className="size-4" />
            Reload
          </Button>
          <Link to="/" reloadDocument className="inline-flex min-h-tap items-center rounded-lg px-4 text-sm font-medium text-fg hover:bg-surface-2">
            Go to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

/** The root error boundary: the whole screen, used when the shell itself fails. */
export function RootError() {
  return <ErrorPanel fullPage />;
}

/** The error boundary inside the shell: the navigation stays usable around the error. */
export function PageError() {
  return <ErrorPanel fullPage={false} />;
}
